class TrAnchorNavigation extends HTMLElement {
  selectors = {
    link: '[data-tr-js-anchor-navigation-link]',
    target: '[data-tr-js-anchor-navigation-target]',
  }

  attributes = {
    topOffset: 'data-tr-anchor-navigation-top-offset',
  }

  stateClasses = {
    isActive: 'is-active',
  }

  connectedCallback() {
    this.linkElements = Array.from(this.querySelectorAll(this.selectors.link))
    this.targetElements = Array.from(this.querySelectorAll(this.selectors.target))
    this.visibleTargetElements = new Set()
    this.linkElementsByTargetId = new Map()
    this.observer = null
    this.isClickScrolling = false
    this.scrollTimeoutId = null

    this.init()
    this.bindEvents()

    document.addEventListener('shopify:section:load', this.onSectionLoad)
    document.addEventListener('shopify:block:select', this.onBlockSelect)
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:section:load', this.onSectionLoad)
    document.removeEventListener('shopify:block:select', this.onBlockSelect)

    this.destroy()
  }

  init() {
    if (!this.linkElements.length || !this.targetElements.length) return

    this.createLinkMap()
    this.createObserver()
    this.setInitialActiveLink()
  }

  bindEvents() {
    this.linkElements.forEach((linkElement) => {
      linkElement.addEventListener('click', this.onLinkClick)
    })

    window.addEventListener('hashchange', this.onHashChange)
    window.addEventListener('scrollend', this.onScrollEnd)
  }

  onSectionLoad = (event) => {
    if (!this.closest(`#shopify-section-${event.detail.sectionId}`)) return

    this.destroy()
    this.init()
    this.bindEvents()
  }

  onBlockSelect = (event) => {
    if (this.dataset.blockId !== event.detail.blockId) return

    this.setActiveLinkByScrollPosition()
  }

  onLinkClick = (event) => {
    const linkElement = event.currentTarget
    const targetId = this.getTargetIdFromLink(linkElement)

    if (targetId === '') return

    this.startClickScrolling()
    this.setActiveLink(targetId)
  }

  onHashChange = () => {
    const targetId = window.location.hash.replace('#', '')

    if (targetId === '') return

    this.setActiveLink(targetId)
  }

  onScrollEnd = () => {
    if (!this.isClickScrolling) return

    this.stopClickScrolling()
  }

  onTargetIntersect = (entries) => {
    this.updateVisibleTargetElements(entries)

    if (this.isClickScrolling) return

    this.setActiveLinkByScrollPosition()
  }

  createLinkMap() {
    this.linkElementsByTargetId.clear()

    this.linkElements.forEach((linkElement) => {
      const targetId = this.getTargetIdFromLink(linkElement)

      if (targetId === '') return

      const linkElements = this.linkElementsByTargetId.get(targetId) || []

      linkElements.push(linkElement)
      this.linkElementsByTargetId.set(targetId, linkElements)
    })
  }

  createObserver() {
    this.observer = new IntersectionObserver(this.onTargetIntersect, {
      rootMargin: `-${this.getTopOffset()}px 0px -70% 0px`,
      threshold: 0,
    })

    this.targetElements.forEach((targetElement) => {
      this.observer.observe(targetElement)
    })
  }

  updateVisibleTargetElements(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        this.visibleTargetElements.add(entry.target)
        return
      }

      this.visibleTargetElements.delete(entry.target)
    })
  }

  startClickScrolling() {
    this.isClickScrolling = true

    clearTimeout(this.scrollTimeoutId)

    this.scrollTimeoutId = setTimeout(() => {
      this.stopClickScrolling()
    }, 1000)
  }

  stopClickScrolling() {
    this.isClickScrolling = false

    clearTimeout(this.scrollTimeoutId)

    this.scrollTimeoutId = null

    this.setActiveLinkByScrollPosition()
  }

  setInitialActiveLink() {
    const targetId = window.location.hash.replace('#', '')

    if (targetId !== '') {
      this.setActiveLink(targetId)
      return
    }

    this.setActiveLinkByScrollPosition()
  }

  setActiveLinkByScrollPosition() {
    const activeTargetElement =
      this.getVisibleTargetElement() || this.getClosestPassedTargetElement()

    if (!activeTargetElement) return

    this.setActiveLink(activeTargetElement.id)
  }

  setActiveLink(targetId) {
    this.linkElements.forEach((linkElement) => {
      linkElement.classList.remove(this.stateClasses.isActive)
    })

    const activeLinkElements = this.linkElementsByTargetId.get(targetId)

    if (!activeLinkElements) return

    activeLinkElements.forEach((linkElement) => {
      linkElement.classList.add(this.stateClasses.isActive)
    })
  }

  getVisibleTargetElement() {
    return this.targetElements.find((targetElement) => {
      return this.visibleTargetElements.has(targetElement)
    })
  }

  getClosestPassedTargetElement() {
    let activeTargetElement = this.targetElements[0]

    this.targetElements.forEach((targetElement) => {
      const targetTop = targetElement.getBoundingClientRect().top

      if (targetTop <= this.getTopOffset()) {
        activeTargetElement = targetElement
      }
    })

    return activeTargetElement
  }

  getTargetIdFromLink(linkElement) {
    const href = linkElement.getAttribute('href') || ''

    if (!href.startsWith('#')) return ''

    return href.replace('#', '')
  }

  getTopOffset() {
    const topOffsetValue = this.getAttribute(this.attributes.topOffset)

    if (topOffsetValue === null || topOffsetValue === '') return 120

    const topOffset = Number(topOffsetValue)

    if (Number.isNaN(topOffset)) return 120

    return topOffset
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }

    this.linkElements.forEach((linkElement) => {
      linkElement.removeEventListener('click', this.onLinkClick)
      linkElement.classList.remove(this.stateClasses.isActive)
    })

    window.removeEventListener('hashchange', this.onHashChange)
    window.removeEventListener('scrollend', this.onScrollEnd)

    clearTimeout(this.scrollTimeoutId)

    this.visibleTargetElements.clear()
    this.linkElementsByTargetId.clear()

    this.scrollTimeoutId = null
    this.isClickScrolling = false
  }
}

if (!customElements.get('tr-anchor-navigation')) {
  customElements.define('tr-anchor-navigation', TrAnchorNavigation)
}